import React, { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { CompanyData } from '../../types/fieldResearch';

interface Props {
  data: CompanyData;
  onChange: (data: CompanyData) => void;
  /** Se true, exibe apenas campos de endereço (CEP + logradouro opcionais) */
  addressOnly?: boolean;
}

const inputCls =
  'w-full bg-[#1a1640] border border-white/15 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-purple-500/60 focus:border-purple-500/60 transition';

const labelCls = 'block text-xs font-semibold text-white/45 uppercase tracking-wider mb-1.5';

export default function CompanyForm({ data, onChange, addressOnly = false }: Props) {
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');

  const upd = (field: keyof CompanyData, value: string) =>
    onChange({ ...data, [field]: value });

  const selectCls =
    'w-full bg-[#1a1640] border border-white/15 text-white rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-purple-500/60 transition';

  // ── Busca CEP ViaCEP ────────────────────────────────────────────
  const handleCepBlur = async () => {
    const cep = data.cep.replace(/\D/g, '');
    if (cep.length !== 8) return;
    setCepLoading(true);
    setCepError('');
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const json = await res.json();
      if (json.erro) {
        setCepError('CEP não encontrado.');
      } else {
        onChange({
          ...data,
          address: json.logradouro || data.address,
          district: json.bairro || data.district,
          city: json.localidade || data.city,
          state: json.uf || data.state,
        });
      }
    } catch (_) {
      setCepError('Erro ao consultar o CEP.');
    } finally {
      setCepLoading(false);
    }
  };

  // ── Modo endereço apenas ─────────────────────────────────────────
  if (addressOnly) {
    return (
      <div className="space-y-4">
        <div>
          <label className={labelCls}>CEP <span className="text-white/25 normal-case font-normal">(opcional)</span></label>
          <div className="relative">
            <input
              className={`${inputCls} pr-10`}
              placeholder="00000-000"
              value={data.cep}
              onChange={(e) => {
                let v = e.target.value.replace(/\D/g, '').slice(0, 8);
                if (v.length > 5) v = v.slice(0, 5) + '-' + v.slice(5);
                upd('cep', v);
                setCepError('');
              }}
              onBlur={handleCepBlur}
              maxLength={9}
              inputMode="numeric"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/30">
              {cepLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            </div>
          </div>
          {cepError && <p className="text-red-400 text-xs mt-1">{cepError}</p>}
          {!cepError && !cepLoading && data.cep.length >= 9 && data.address && (
            <p className="text-green-400 text-xs mt-1">✓ Endereço preenchido automaticamente</p>
          )}
        </div>

        <div>
          <label className={labelCls}>Endereço <span className="text-white/25 normal-case font-normal">(opcional)</span></label>
          <input
            className={inputCls}
            placeholder="Rua, Av., número..."
            value={data.address}
            onChange={(e) => upd('address', e.target.value)}
          />
        </div>
      </div>
    );
  }

  // ── Modo completo (identificação + sistema) ──────────────────────
  return (
    <div className="space-y-4">
      <div>
        <label className={labelCls}>Nome do estabelecimento *</label>        <input
          className={inputCls}
          placeholder="Ex: Salão da Maria"
          value={data.name}
          onChange={(e) => upd('name', e.target.value)}
          autoFocus
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Responsável</label>
          <input
            className={inputCls}
            placeholder="Nome"
            value={data.contactName}
            onChange={(e) => upd('contactName', e.target.value)}
          />
        </div>
        <div>
          <label className={labelCls}>WhatsApp</label>
          <input
            className={inputCls}
            placeholder="(11) 9 0000-0000"
            value={data.phone}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
              let v = digits;
              if (digits.length > 10) {
                v = `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7)}`;
              } else if (digits.length > 6) {
                v = `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
              } else if (digits.length > 2) {
                v = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
              } else if (digits.length > 0) {
                v = `(${digits}`;
              }
              upd('phone', v);
            }}
            type="tel"
            maxLength={16}
          />
        </div>
      </div>

      <div>
        <label className={labelCls}>Instagram</label>
        <input
          className={inputCls}
          placeholder="@usuario"
          value={data.instagram}
          onChange={(e) => upd('instagram', e.target.value)}
        />
      </div>

      {/* Sistema atual */}
      <div className="space-y-3 pt-1">
        <p className="text-xs font-semibold text-white/45 uppercase tracking-wider">Sistema Atual</p>

        <div>
          <label className={labelCls}>Usa sistema de gestão / agendamento?</label>
          <div className="grid grid-cols-3 gap-2">
            {(['', 'sim', 'nao'] as const).map((val) => {
              const labels: Record<string, string> = { '': 'Não sei', sim: 'Sim ✅', nao: 'Não ❌' };
              return (
                <button
                  key={val}
                  type="button"
                  onClick={() =>
                    onChange({
                      ...data,
                      usesSystem: val,
                      systemName: val === 'nao' ? '' : data.systemName,
                      systemPrice: val === 'nao' ? '' : data.systemPrice,
                      systemPricingType: val === 'nao' ? '' : data.systemPricingType,
                    })
                  }
                  className={`py-3 rounded-xl border text-sm font-medium transition-all active:scale-95 ${
                    data.usesSystem === val
                      ? 'bg-purple-600/25 border-purple-500/60 text-white'
                      : 'bg-white/5 border-white/10 text-white/55 hover:bg-white/10'
                  }`}
                >
                  {labels[val]}
                </button>
              );
            })}
          </div>
        </div>

        {data.usesSystem === 'sim' && (
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Nome do sistema</label>
              <input
                className={inputCls}
                placeholder="Ex: Trinks, SetaDigital, Studio Soft..."
                value={data.systemName}
                onChange={(e) => upd('systemName', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Valor (R$)</label>
                <input
                  className={inputCls}
                  placeholder="0,00"
                  value={data.systemPrice}
                  onChange={(e) => upd('systemPrice', e.target.value)}
                  inputMode="decimal"
                />
              </div>
              <div>
                <label className={labelCls}>Frequência</label>
                <select
                  className={selectCls}
                  value={data.systemPricingType}
                  onChange={(e) => upd('systemPricingType', e.target.value)}
                >
                  <option value="">Selecione</option>
                  <option value="mensal">Mensal</option>
                  <option value="unico">Pagamento único</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
